/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */

/**
 * Side Bar Component
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @version 1.11.0
 * @since 1.1.0
 */

import { ReactElement, useCallback } from 'react';

import Guard from '../../../modules/role/Guard';
import { fonts } from '../../../theme';
import { TextBlock } from '../index';
import SideBarItem from './SideBarItem';
import { SideBarNavItemStyled, SideBarNavStyled } from './styled/SideBarItemStyled';
import { ISideBarItem } from './types/SideBarTypes';

export interface ISideBarNavProps {
    activeItemValue: string;
    children?: ReactElement<ISideBarItem> | Array<ReactElement<ISideBarItem>>;
    firstItem?: ISideBarItem;
    items: ISideBarItem[];
    onClick?: (item: ISideBarItem) => void;
}

const SideBarNav = (props: ISideBarNavProps) => {
    const onClickHandler = useCallback((item: ISideBarItem | undefined): void => {
        if (props.onClick && item) {
            props.onClick(item);
        }
    }, [props.onClick]);

    return (
        <SideBarNavStyled>
            {
                props.firstItem &&
                <SideBarNavItemStyled
                    data-index={props.firstItem.value}
                    isActive={props.firstItem.value === props.activeItemValue}
                    onClick={() => onClickHandler(props.firstItem)}
                >
                    <TextBlock
                        font={props.firstItem.value === props.activeItemValue ? fonts.textMainBold : fonts.textMain}
                    >
                        {props.firstItem.label}
                    </TextBlock>
                </SideBarNavItemStyled>
            }
            {
                props.items.map((item: ISideBarItem) => {
                        return item.accessKey
                            ? <Guard accessKey={item.accessKey}
                                     key={item.value}>
                                <SideBarItem
                                    isActive={item.value === props.activeItemValue}
                                    isExternalUrl={item.isExternalUrl}
                                    item={item}
                                    onClick={() => onClickHandler(item)}
                                />
                            </Guard>
                            : <SideBarItem
                                isActive={item.value === props.activeItemValue}
                                isExternalUrl={item.isExternalUrl}
                                item={item}
                                key={item.value}
                                onClick={() => onClickHandler(item)}
                            />
                    },
                )
            }
        </SideBarNavStyled>
    );
};

SideBarNav.defaultProps = {
    labelName: 'label',
    valueName: 'value',
};

export default SideBarNav;
